import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  ArrowLeft, Send, Bot, User, Sparkles, RefreshCw,
  TrendingUp, Shield, Trash2, MessageSquare, Brain,
  Zap, Target, AlertTriangle, CheckCircle2, Clock,
  DollarSign, PieChart, ArrowRight, Loader2, Wallet
} from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { API } from "../App";

// AI Provider logos/colors
const AI_PROVIDERS = {
  claude: { name: "Claude", color: "#D97706", company: "Anthropic" },
  gpt4: { name: "GPT-4", color: "#10B981", company: "OpenAI" },
  grok: { name: "Grok", color: "#6366F1", company: "xAI" },
  gemini: { name: "Gemini", color: "#3B82F6", company: "Google" },
};

// 4-AI Deliberation Panel Component
const DeliberationPanel = ({ isAnalyzing, providers, analyses }) => {
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      className="mb-4"
    >
      <Card className="glass-card border-[#FFD700]/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-white/80 flex items-center gap-2">
            <Brain className="w-4 h-4 text-[#FFD700]" />
            4-AI Consensus Engine
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-2">
            {Object.entries(AI_PROVIDERS).map(([id, provider]) => {
              const analysis = analyses?.find(a => 
                a.provider === id || a.provider?.toLowerCase() === id
              );
              const isComplete = analysis && !analysis.error;
              const isError = analysis?.error;
              
              return (
                <div
                  key={id}
                  className={`p-3 rounded-lg border transition-all duration-300 ${
                    isComplete 
                      ? "border-green-500/50 bg-green-500/10" 
                      : isError
                        ? "border-red-500/50 bg-red-500/10"
                        : isAnalyzing
                          ? "border-white/20 bg-white/5 animate-pulse"
                          : "border-white/10 bg-white/5"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span 
                      className="text-xs font-bold"
                      style={{ color: provider.color }}
                    >
                      {provider.name}
                    </span>
                    {isComplete && (
                      <CheckCircle2 className="w-3 h-3 text-green-500" />
                    )}
                    {isError && (
                      <AlertTriangle className="w-3 h-3 text-red-500" />
                    )}
                    {isAnalyzing && !analysis && (
                      <Loader2 className="w-3 h-3 text-white/50 animate-spin" />
                    )}
                  </div>
                  <p className="text-[10px] text-white/40">{provider.company}</p>
                  {isComplete && analysis.confidence && (
                    <div className="mt-2">
                      <div className="flex justify-between text-[10px] text-white/60">
                        <span>Confidence</span>
                        <span>{Math.round(analysis.confidence * 100)}%</span>
                      </div>
                      <div className="w-full h-1 bg-white/10 rounded-full mt-1">
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{ 
                            width: `${analysis.confidence * 100}%`,
                            backgroundColor: provider.color 
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {isAnalyzing && (
            <p className="text-center text-white/40 text-xs mt-3">
              <Loader2 className="w-3 h-3 inline mr-1 animate-spin" />
              AIs are analyzing your request...
            </p>
          )}

          {/* Per-AI Reasoning Summaries */}
          {!isAnalyzing && analyses?.some(a => a.reasoning) && (
            <div className="mt-3 space-y-2">
              <p className="text-xs text-white/50 font-medium">Individual Perspectives:</p>
              {analyses.filter(a => a.reasoning).map((a, i) => {
                const prov = AI_PROVIDERS[a.provider];
                return (
                  <div key={i} className="p-2 bg-white/5 rounded-lg border border-white/10">
                    <span className="text-[10px] font-bold" style={{ color: prov?.color || '#fff' }}>
                      {prov?.name || a.provider}:
                    </span>
                    <p className="text-[11px] text-white/60 mt-0.5 line-clamp-2">{a.reasoning}</p>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Strategy Card Component
const StrategyCard = ({ strategy, onApprove, onReject, onModify, portfolioValue = 10000 }) => {
  if (!strategy) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-4"
    >
      <Card className="glass-card border-[#FFD700]/30">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-bold text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-[#FFD700]" />
              Recommended Strategy
            </CardTitle>
            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">
              {Math.round((strategy.consensus_confidence || strategy.confidence || 0.85) * 100)}% Consensus
            </span>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Allocations */}
          <div className="space-y-2">
            {strategy.allocations?.map((alloc, i) => (
              <div key={i} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-2 h-2 rounded-full"
                    style={{ 
                      backgroundColor: alloc.protocol === "Liquid Reserve" 
                        ? "#6B7280" 
                        : "#FFD700" 
                    }}
                  />
                  <span className="text-white text-sm">{alloc.protocol}</span>
                  <span className="text-white/40 text-xs">({alloc.chain?.toUpperCase()})</span>
                </div>
                <div className="text-right">
                  <span className="text-white font-medium">{alloc.percentage}%</span>
                  {alloc.expected_apy > 0 && (
                    <span className="text-green-400 text-xs ml-2">
                      {alloc.expected_apy.toFixed(1)}% APY
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 bg-white/5 rounded-lg">
              <TrendingUp className="w-4 h-4 text-green-400 mx-auto mb-1" />
              <p className="text-lg font-bold text-white">
                {strategy.expected_apy?.toFixed(1) || strategy.recommendation?.expected_apy?.toFixed(1)}%
              </p>
              <p className="text-xs text-white/40">Expected APY</p>
            </div>
            <div className="text-center p-3 bg-white/5 rounded-lg">
              <DollarSign className="w-4 h-4 text-[#FFD700] mx-auto mb-1" />
              <p className="text-lg font-bold text-white">
                ${(() => {
                  const apy = strategy.expected_apy || strategy.recommendation?.expected_apy || 0;
                  const portfolio = strategy.portfolio_value || portfolioValue || 10000;
                  const yieldUsd = strategy.expected_annual_yield_usd || (portfolio * apy / 100);
                  return yieldUsd > 0 ? yieldUsd.toFixed(0) : "—";
                })()}
              </p>
              <p className="text-xs text-white/40">Annual Yield</p>
            </div>
            <div className="text-center p-3 bg-white/5 rounded-lg">
              <Shield className="w-4 h-4 text-blue-400 mx-auto mb-1" />
              <p className="text-lg font-bold text-white">
                {(() => {
                  const rs = strategy.risk_score ?? strategy.recommendation?.risk_score
                    ?? strategy.risk_assessment?.score ?? strategy.risk_level;
                  if (typeof rs === 'number') return rs.toFixed(0);
                  if (typeof rs === 'string') {
                    const map = { low: 20, conservative: 25, moderate: 50, aggressive: 75, high: 80 };
                    return map[rs.toLowerCase()] || rs;
                  }
                  return "—";
                })()}
              </p>
              <p className="text-xs text-white/40">Risk Score</p>
            </div>
          </div>

          {/* AI Agreement */}
          {strategy.key_agreements && strategy.key_agreements.length > 0 && (
            <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
              <p className="text-green-400 text-xs font-medium mb-1">
                <CheckCircle2 className="w-3 h-3 inline mr-1" />
                All 4 AIs Agree:
              </p>
              <ul className="text-white/70 text-xs space-y-1">
                {strategy.key_agreements.slice(0, 3).map((agreement, i) => (
                  <li key={i}>• {agreement}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Concerns */}
          {strategy.concerns && strategy.concerns.length > 0 && (
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <p className="text-yellow-400 text-xs font-medium mb-1">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                Points to Consider:
              </p>
              <ul className="text-white/70 text-xs space-y-1">
                {strategy.concerns.slice(0, 2).map((concern, i) => (
                  <li key={i}>• {concern}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Strategy Rationale / Detailed Breakdown */}
          {(strategy.rationale || strategy.recommendation?.rationale || strategy.explanation) && (
            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-blue-400 text-xs font-medium mb-1">
                <Brain className="w-3 h-3 inline mr-1" />
                Strategy Rationale:
              </p>
              <p className="text-white/70 text-xs leading-relaxed">
                {strategy.rationale || strategy.recommendation?.rationale || strategy.explanation}
              </p>
            </div>
          )}

          {/* Dissenting Views */}
          {strategy.dissenting_views && strategy.dissenting_views.length > 0 && (
            <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
              <p className="text-purple-400 text-xs font-medium mb-1">
                <MessageSquare className="w-3 h-3 inline mr-1" />
                Alternative Perspectives:
              </p>
              <ul className="text-white/70 text-xs space-y-1">
                {strategy.dissenting_views.slice(0, 2).map((view, i) => (
                  <li key={i}>• {view}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button 
              className="flex-1 btn-gold"
              onClick={() => onApprove(strategy)}
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Approve Strategy
            </Button>
            <Button 
              variant="outline"
              className="border-red-500/30 text-red-400/70 hover:text-red-300 hover:border-red-500/50"
              onClick={() => onReject(strategy)}
            >
              Reject
            </Button>
            <Button 
              variant="outline"
              className="border-white/20 text-white/70 hover:text-white"
              onClick={() => onModify(strategy)}
            >
              Modify
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const AIAssistant = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [showDeliberation, setShowDeliberation] = useState(false);
  const [currentAnalyses, setCurrentAnalyses] = useState([]);
  const [currentStrategy, setCurrentStrategy] = useState(null);
  const [portfolioValue, setPortfolioValue] = useState(0);
  const [modifyingStrategy, setModifyingStrategy] = useState(null);
  const [userWallets, setUserWallets] = useState([]);
  const [userPrices, setUserPrices] = useState({});
  const [portfolioContext, setPortfolioContext] = useState(null);
  const [portfolioLoading, setPortfolioLoading] = useState(true);
  const [userName, setUserName] = useState("");
  const [userId, setUserId] = useState("");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const getAuthConfig = () => {
    const token = localStorage.getItem('auth_token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  // Fetch user info + real portfolio data on mount
  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        const config = getAuthConfig();
        const [userRes, walletsRes, pricesRes] = await Promise.all([
          axios.get(`${API}/auth/me`, config).catch(() => null),
          axios.get(`${API}/wallets`, config),
          axios.get(`${API}/prices`, config)
        ]);

        // Extract first name and user ID
        if (userRes?.data?.name) {
          const firstName = userRes.data.name.split(' ')[0];
          setUserName(firstName);
        }
        if (userRes?.data?._id || userRes?.data?.id || userRes?.data?.user_id) {
          setUserId(userRes.data._id || userRes.data.id || userRes.data.user_id);
        }

        const wallets = walletsRes.data.wallets || [];
        const prices = pricesRes.data.prices || {};

        setUserWallets(wallets);
        setUserPrices(prices);

        // Build portfolio context for Xavier
        const holdings = wallets
          .filter(w => (w.balance || 0) > 0)
          .map(w => {
            const price = prices[w.chain]?.price_usd || 0;
            const value = (w.balance || 0) * price;
            const change24h = prices[w.chain]?.change_24h || 0;
            return {
              chain: w.chain,
              token: w.chain.toUpperCase(),
              balance: w.balance,
              price_usd: price,
              value_usd: value,
              change_24h: change24h,
              is_testnet: w.is_testnet || false,
              network: w.network || null
            };
          });

        const totalValue = holdings.reduce((sum, h) => sum + h.value_usd, 0);
        const emptyWallets = wallets.filter(w => !w.balance || w.balance === 0).map(w => w.chain.toUpperCase());
        const trackedButNoWallet = Object.keys(prices)
          .filter(chain => !wallets.find(w => w.chain === chain))
          .map(chain => chain.toUpperCase());

        const context = {
          total_value_usd: totalValue,
          holdings: holdings.map(h => ({
            ...h,
            percentage: totalValue > 0 ? ((h.value_usd / totalValue) * 100).toFixed(1) : 0
          })),
          empty_wallets: emptyWallets,
          tracked_no_wallet: trackedButNoWallet,
          wallet_count: wallets.length,
          chains_with_funds: holdings.length,
          // Human-readable summary for the AI prompt
          summary: `User portfolio: $${totalValue.toFixed(2)} total. ` +
            holdings.map(h => 
              `${h.balance.toLocaleString()} ${h.token} ($${h.value_usd.toFixed(2)}, ${totalValue > 0 ? ((h.value_usd / totalValue) * 100).toFixed(1) : 0}%)`
            ).join(', ') +
            (emptyWallets.length > 0 ? `. Empty wallets: ${emptyWallets.join(', ')}` : '') +
            (trackedButNoWallet.length > 0 ? `. No wallet created for: ${trackedButNoWallet.join(', ')}` : '')
        };

        setPortfolioContext(context);
        setPortfolioValue(totalValue);
      } catch (error) {
        console.error("Failed to fetch portfolio for Xavier:", error);
        // Continue without portfolio context — Xavier will use defaults
      } finally {
        setPortfolioLoading(false);
      }
    };

    fetchPortfolio();
  }, []);

  useEffect(() => {
    // Load saved messages from localStorage
    const savedMessages = localStorage.getItem('xavier_messages');
    const savedSessionId = localStorage.getItem('xavier_session_id');
    
    if (savedMessages) {
      try {
        const parsed = JSON.parse(savedMessages);
        if (parsed.length > 0) {
          setMessages(parsed);
          if (savedSessionId) setSessionId(savedSessionId);
          return; // Skip fetching intro if we have saved messages
        }
      } catch (e) {
        console.warn('Failed to parse saved messages');
      }
    }

    // Fetch Xavier's intro only if no saved messages
    const fetchIntro = async () => {
      try {
        const response = await axios.get(`${API}/xavier/intro`);
        setMessages([{
          role: "assistant",
          content: response.data.introduction,
          timestamp: new Date().toISOString(),
          suggestedPrompts: response.data.suggested_prompts
        }]);
      } catch (error) {
        // Fallback intro
        setMessages([{
          role: "assistant",
          content: "👋 Hello! I'm Xavier, your AI financial advisor. I consult with four leading AI models - Claude, GPT-4, Grok, and Gemini - to give you the best yield optimization strategies.\n\nWhat would you like help with today?",
          timestamp: new Date().toISOString(),
          suggestedPrompts: [
            "I want to grow $10,000 safely over 12 months",
            "What are the best yield opportunities right now?",
            "Help me diversify across multiple chains"
          ]
        }]);
      }
    };
    
    fetchIntro();
    inputRef.current?.focus();
  }, []);

  // Persist messages to localStorage whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('xavier_messages', JSON.stringify(messages));
    }
  }, [messages]);

  // Persist session ID
  useEffect(() => {
    if (sessionId) {
      localStorage.setItem('xavier_session_id', sessionId);
    }
  }, [sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (e, overrideInput = null) => {
    e?.preventDefault();
    const messageText = overrideInput || input;
    if (!messageText.trim() || loading) return;

    const userMessage = {
      role: "user",
      content: messageText.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);
    setCurrentAnalyses([]);
    setCurrentStrategy(null);

    try {
      const config = getAuthConfig();
      
      // Always use /chat — backend smart-routes yield_strategy to consensus engine
      const response = await axios.post(`${API}/xavier/chat`, {
        message: messageText,
        portfolio_context: portfolioContext || null,
        user_name: userName || null,
        user_id: userId || null,
        conversation_history: messages.slice(-10).map(m => ({
          role: m.role,
          content: m.content
        }))
      }, config);

      const data = response.data;
      const isStrategyResponse = data.question_type === "yield_strategy" && data.allocations?.length > 0;

      if (isStrategyResponse) {
        // Backend ran the 4-AI consensus engine — show full strategy UI
        setShowDeliberation(true);
        setAnalyzing(false);

        // Populate deliberation panel with AI analyses
        if (data.ai_analyses && data.ai_analyses.length > 0) {
          setCurrentAnalyses(data.ai_analyses.map(a => {
            let provider = (a.provider || a.model || a.source || '').toLowerCase();
            if (provider.includes('claude') || provider.includes('anthropic')) provider = 'claude';
            else if (provider.includes('gpt') || provider.includes('openai')) provider = 'gpt4';
            else if (provider.includes('grok') || provider.includes('xai')) provider = 'grok';
            else if (provider.includes('gemini') || provider.includes('google')) provider = 'gemini';
            
            return {
              provider,
              confidence: a.confidence ?? a.score ?? data.confidence ?? 0.85,
              reasoning: a.reasoning || a.summary || a.response || null,
              complete: true
            };
          }));
        } else if (data.ai_sources) {
          const baseConf = data.confidence || 0.85;
          setCurrentAnalyses(data.ai_sources.map((source, idx) => {
            let provider = (typeof source === 'string' ? source : source.provider || source.model || '').toLowerCase();
            if (provider.includes('claude') || provider.includes('anthropic')) provider = 'claude';
            else if (provider.includes('gpt') || provider.includes('openai')) provider = 'gpt4';
            else if (provider.includes('grok') || provider.includes('xai')) provider = 'grok';
            else if (provider.includes('gemini') || provider.includes('google')) provider = 'gemini';
            
            return {
              provider,
              confidence: typeof source === 'object' ? (source.confidence ?? baseConf) : baseConf,
              complete: true
            };
          }));
        }

        // Extract portfolio value from message or use real portfolio
        const portfolioMatch = messageText.match(/\$?([\d,]+(?:\.\d+)?)\s*(?:k|K|thousand|million|M)?/);
        let parsedPortfolioValue = portfolioContext?.total_value_usd || 10000;
        if (portfolioMatch) {
          let value = parseFloat(portfolioMatch[1].replace(/,/g, ''));
          if (messageText.toLowerCase().includes('k') || messageText.toLowerCase().includes('thousand')) value *= 1000;
          if (messageText.toLowerCase().includes('million') || messageText.toLowerCase().includes('m ')) value *= 1000000;
          parsedPortfolioValue = value;
        }
        setPortfolioValue(parsedPortfolioValue);

        // Set strategy card data
        setCurrentStrategy(data);

        setMessages(prev => [...prev, {
          role: "assistant",
          content: data.message || data.response,
          timestamp: data.timestamp,
          strategy: data
        }]);
      } else {
        // Regular chat response (price, news, sentiment, explanation, etc.)
        setShowDeliberation(false);
        
        setMessages(prev => [...prev, {
          role: "assistant",
          content: data.response,
          timestamp: data.timestamp
        }]);
      }
    } catch (error) {
      console.error("Chat error:", error);
      setShowDeliberation(false);
      
      // Fallback to old endpoint if Xavier isn't available
      try {
        const config = getAuthConfig();
        const response = await axios.post(`${API}/ai/chat`, {
          message: messageText,
          session_id: sessionId
        }, config);

        setSessionId(response.data.session_id);
        setMessages(prev => [...prev, {
          role: "assistant",
          content: response.data.response,
          timestamp: response.data.timestamp
        }]);
      } catch (fallbackError) {
        toast.error("Failed to get response");
        setMessages(prev => [...prev, {
          role: "assistant",
          content: "I apologize, but I encountered an error. Please try again.",
          timestamp: new Date().toISOString(),
          error: true
        }]);
      }
    } finally {
      setLoading(false);
      setAnalyzing(false);
      inputRef.current?.focus();
    }
  };

  const handleApproveStrategy = async (strategy) => {
    toast.success("Strategy approved! Xavier is learning your preferences.");
    
    // Record the approval in Xavier's memory for Adaptive Risk Profiling
    try {
      await axios.post(`${API}/xavier/strategy-action`, {
        user_id: userId || "",
        action: "approved",
        intent: strategy.summary || "",
        portfolio_value: portfolioContext?.total_value_usd || portfolioValue || 0,
        expected_apy: strategy.expected_apy || strategy.recommendation?.expected_apy || 0,
        risk_score: strategy.risk_score || strategy.recommendation?.risk_score || 0,
        risk_tolerance: strategy.risk_tolerance || "moderate",
        allocations: strategy.allocations || strategy.recommendation?.allocations || [],
        chains: (strategy.allocations || []).map(a => a.chain).filter(Boolean),
        protocols: (strategy.allocations || []).map(a => a.protocol).filter(Boolean),
      }, getAuthConfig());
    } catch (error) {
      console.warn("Failed to record strategy approval:", error);
    }
  };

  const handleRejectStrategy = async (strategy) => {
    toast.info("Noted. Xavier will adjust future recommendations.");
    
    try {
      await axios.post(`${API}/xavier/strategy-action`, {
        user_id: userId || "",
        action: "rejected",
        intent: strategy.summary || "",
        portfolio_value: portfolioContext?.total_value_usd || portfolioValue || 0,
        expected_apy: strategy.expected_apy || strategy.recommendation?.expected_apy || 0,
        risk_score: strategy.risk_score || strategy.recommendation?.risk_score || 0,
        risk_tolerance: strategy.risk_tolerance || "moderate",
        allocations: strategy.allocations || strategy.recommendation?.allocations || [],
        chains: (strategy.allocations || []).map(a => a.chain).filter(Boolean),
        protocols: (strategy.allocations || []).map(a => a.protocol).filter(Boolean),
      }, getAuthConfig());
    } catch (error) {
      console.warn("Failed to record strategy rejection:", error);
    }
  };

  const handleModifyStrategy = (strategy) => {
    setModifyingStrategy(strategy);
    const modifyPrompt = `I'd like to modify the strategy. Current allocations: ${
      strategy.allocations?.map(a => `${a.protocol} ${a.percentage}%`).join(', ') || 'as shown'
    }. What changes would you suggest?`;
    setInput(modifyPrompt);
    inputRef.current?.focus();
    toast.info("Edit the modification request below and send it to Xavier.");

    // Record the modify action
    try {
      axios.post(`${API}/xavier/strategy-action`, {
        user_id: userId || "",
        action: "modified",
        intent: strategy.summary || "",
        portfolio_value: portfolioContext?.total_value_usd || portfolioValue || 0,
        expected_apy: strategy.expected_apy || strategy.recommendation?.expected_apy || 0,
        risk_score: strategy.risk_score || strategy.recommendation?.risk_score || 0,
        allocations: strategy.allocations || strategy.recommendation?.allocations || [],
        chains: (strategy.allocations || []).map(a => a.chain).filter(Boolean),
        protocols: (strategy.allocations || []).map(a => a.protocol).filter(Boolean),
      }, getAuthConfig());
    } catch (error) {
      console.warn("Failed to record strategy modification:", error);
    }
  };

  const clearChat = () => {
    setMessages([{
      role: "assistant",
      content: "Chat cleared. How can I help you today?",
      timestamp: new Date().toISOString()
    }]);
    setSessionId(null);
    setShowDeliberation(false);
    setCurrentStrategy(null);
    setCurrentAnalyses([]);
    setModifyingStrategy(null);
    localStorage.removeItem('xavier_messages');
    localStorage.removeItem('xavier_session_id');
    toast.success("Chat cleared");
  };

  const quickPrompts = portfolioContext && portfolioContext.holdings.length > 0
    ? [
        { icon: TrendingUp, text: "Optimize my portfolio", prompt: "Looking at my current portfolio, how can I optimize it for better yields?" },
        { icon: Shield, text: "Risk analysis", prompt: "Analyze the risk in my current portfolio and suggest improvements" },
        { icon: PieChart, text: "Diversify", prompt: "My portfolio needs better diversification — what do you recommend?" },
      ]
    : [
        { icon: TrendingUp, text: "Grow safely", prompt: "I want to grow $10,000 safely over 12 months" },
        { icon: Zap, text: "Best yields", prompt: "What are the best yield opportunities right now?" },
        { icon: PieChart, text: "Diversify", prompt: "Help me diversify across multiple chains" },
      ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#020617] via-[#000A1F] to-[#001133] flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/5 bg-[#020617]/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="text-white/60 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FDB931] flex items-center justify-center">
                  <Bot className="w-6 h-6 text-[#002366]" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-white font-['Syne']">Xavier</h1>
                  <p className="text-white/40 text-xs">4-AI Consensus Engine</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={clearChat}
                className="text-white/60 hover:text-red-400"
              >
                <Trash2 className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Chat Area */}
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-4 flex flex-col">
        
        {/* Portfolio Context Banner */}
        {portfolioContext && portfolioContext.holdings.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-3"
          >
            <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-[#FFD700]/5 border border-[#FFD700]/20">
              <Wallet className="w-4 h-4 text-[#FFD700] flex-shrink-0" />
              <div className="flex items-center gap-3 overflow-x-auto text-xs">
                <span className="text-[#FFD700] font-bold whitespace-nowrap">
                  ${portfolioContext.total_value_usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <span className="text-white/20">|</span>
                {portfolioContext.holdings.map((h, i) => (
                  <span key={i} className="text-white/50 whitespace-nowrap">
                    {h.balance.toLocaleString()} {h.token}
                    <span className="text-white/30 ml-1">(${h.value_usd.toFixed(0)})</span>
                    <span className={`ml-1 ${h.change_24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {h.change_24h >= 0 ? '↑' : '↓'}{Math.abs(h.change_24h).toFixed(1)}%
                    </span>
                  </span>
                ))}
              </div>
              <span className="text-white/20 text-[10px] whitespace-nowrap ml-auto">LIVE</span>
            </div>
          </motion.div>
        )}
        {portfolioLoading && (
          <div className="mb-3 flex items-center gap-2 px-4 py-2 text-white/30 text-xs">
            <Loader2 className="w-3 h-3 animate-spin" />
            Loading portfolio...
          </div>
        )}
        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-4 pb-4">
            {/* Deliberation Panel */}
            <AnimatePresence>
              {showDeliberation && (
                <DeliberationPanel 
                  isAnalyzing={analyzing}
                  providers={AI_PROVIDERS}
                  analyses={currentAnalyses}
                />
              )}
            </AnimatePresence>

            <AnimatePresence>
              {messages.map((msg, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FDB931] flex items-center justify-center flex-shrink-0">
                      <Bot className="w-5 h-5 text-[#002366]" />
                    </div>
                  )}

                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-[#FFD700] text-[#002366]'
                      : msg.error
                        ? 'bg-red-500/20 border border-red-500/30 text-white'
                        : 'glass-card text-white'
                  }`}>
                    {msg.role === 'assistant' ? (
                      <div className="prose prose-invert prose-sm max-w-none">
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                            ul: ({ children }) => <ul className="list-disc pl-4 mb-2">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal pl-4 mb-2">{children}</ol>,
                            li: ({ children }) => <li className="mb-1">{children}</li>,
                            strong: ({ children }) => <strong className="text-[#FFD700]">{children}</strong>,
                            code: ({ children }) => <code className="bg-black/30 px-1 rounded text-[#00FFFF]">{children}</code>,
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <p className="text-sm font-medium">{msg.content}</p>
                    )}

                    {/* Suggested Prompts */}
                    {msg.suggestedPrompts && index === 0 && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <p className="text-xs text-white/40 mb-2">Try asking:</p>
                        <div className="flex flex-wrap gap-2">
                          {msg.suggestedPrompts.map((prompt, i) => (
                            <button
                              key={i}
                              onClick={() => {
                                setInput(prompt);
                                setTimeout(() => sendMessage(null, prompt), 100);
                              }}
                              className="text-xs bg-white/10 hover:bg-white/20 text-white/70 hover:text-white px-2 py-1 rounded-full transition-colors"
                            >
                              {prompt}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {msg.role === 'user' && (
                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-white" />
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Strategy Card */}
            {currentStrategy && !analyzing && (
              <StrategyCard 
                strategy={currentStrategy} 
                onApprove={handleApproveStrategy}
                onReject={handleRejectStrategy}
                onModify={handleModifyStrategy}
                portfolioValue={portfolioValue}
              />
            )}

            {loading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex gap-3"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FDB931] flex items-center justify-center">
                  <Bot className="w-5 h-5 text-[#002366]" />
                </div>
                <div className="glass-card rounded-2xl px-4 py-3">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-[#FFD700] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-[#FFD700] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-[#FFD700] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </motion.div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Quick Prompts */}
        {messages.length <= 1 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {quickPrompts.map((item, index) => (
              <Button
                key={index}
                variant="outline"
                size="sm"
                onClick={() => {
                  setInput(item.prompt);
                  setTimeout(() => sendMessage(null, item.prompt), 100);
                }}
                className="border-white/10 text-white/70 hover:text-white hover:border-[#FFD700]/30"
              >
                <item.icon className="w-4 h-4 mr-2" />
                {item.text}
              </Button>
            ))}
          </div>
        )}

        {/* Input Area */}
        <form onSubmit={sendMessage} className="mt-auto">
          <Card className="glass-card">
            <CardContent className="p-3">
              <div className="flex gap-3">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask Xavier for yield advice..."
                  className="flex-1 bg-transparent border-0 text-white placeholder:text-white/30 focus-visible:ring-0"
                  disabled={loading}
                />
                <Button
                  type="submit"
                  disabled={!input.trim() || loading}
                  className="btn-gold rounded-lg"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
          <p className="text-center text-white/20 text-xs mt-2">
            Powered by 4-AI Consensus: Claude • GPT-4 • Grok • Gemini
          </p>
        </form>
      </main>
    </div>
  );
};

export default AIAssistant;
